import { VestingSecuritySummary } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestorDetails/components/VestingSecuritySummary.tsx';
import {
	VESTING_TERMS_BULK_FORM_FIELD,
	VestingTermsBulkFormDto,
	useVestingTermsBulkDrawerController,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestorDetails/components/useVestingTermsBulkDrawerController.ts';
import { GenericDrawer } from '@dealstack/frontend/shared/components/GenericDrawer/GenericDrawer';
import { InfoBox } from '@dealstack/frontend/shared/components/InfoBox/InfoBox';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon.tsx';
import { InlineLoader } from '@dealstack/frontend/shared/components/loaders/InlineLoader/InlineLoader';
import { MantineDateInput } from '@dealstack/frontend/shared/components/mantineForm/MantineDateInput.tsx';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { drawerTransitionProps } from '@dealstack/frontend/shared/mechanism/styleguide/GlobalStyling.tsx';
import { browserDateToMidnightUTC } from '@dealstack/frontend/shared/utils/utc.ts';
import {
	BulkUpdateInvestorsVestingDto,
	InvestmentSecurityRes,
	VestingTermSimplifiedRes,
} from '@dealstack/shared/lib/generated';
import { Button, Flex, Text } from '@mantine/core';
import { useMemo } from 'react';
import { FormProvider } from 'react-hook-form';
import { Select } from 'react-hook-form-mantine';
import { RippleColor } from '@dealstack/frontend/shared/components/RippleIcon/RippleIcon.types';

interface Props {
	opened: boolean;
	onClose: () => void;
	vestingTerms: VestingTermSimplifiedRes[];
	securitiesList: InvestmentSecurityRes[];
	loading?: boolean;
	updateVestingTerms: (args: Omit<BulkUpdateInvestorsVestingDto, 'investorIds'>) => void;
	defaultVestingTermId?: string | null;
}

export const VestingTermsBulkDrawer = ({
	opened,
	onClose,
	vestingTerms,
	securitiesList,
	loading,
	updateVestingTerms,
	defaultVestingTermId,
}: Props) => {
	const tMipManage = useTranslationMipManage();
	const { vestingTermBulkForm, resetForm, formVestingTerm } =
		useVestingTermsBulkDrawerController(defaultVestingTermId);

	const submitBulkUpdates = (formValues: VestingTermsBulkFormDto) => {
		updateVestingTerms({
			vestingTermId: formValues.vestingTermId ?? null,
			startDate: formValues.startDate ? browserDateToMidnightUTC(formValues.startDate) : null,
		});
	};
	const isNoneSubjectToVesting = useMemo(() => {
		return securitiesList.every((s) => !s.isSubjectToVesting);
	}, [securitiesList]);

	const closeDrawerAndResetForm = () => {
		onClose();
		resetForm();
	};

	return (
		<GenericDrawer
			opened={opened}
			onClose={onClose}
			transitionProps={drawerTransitionProps}
			position="right"
			title={tMipManage('vesting_terms_bulk_drawer.title')}
			body={
				<Flex direction="column" h="100%" gap="2rem">
					{loading ? <InlineLoader /> : null}

					{isNoneSubjectToVesting ? (
						<VestingDrawerInfoBox
							rippleColor="gray"
							text={tMipManage(
								'vesting_terms_bulk_drawer.no_vesting_securities_info'
							)}
						/>
					) : (
						<Flex direction="column" gap=".5rem">
							<VestingDrawerInfoBox
								rippleColor="orange"
								text={tMipManage('vesting_terms_bulk_drawer.override_info')}
							/>
							<VestingDrawerInfoBox
								text={tMipManage('vesting_terms_bulk_drawer.securities_info')}
							/>
						</Flex>
					)}

					<Flex direction="column" gap="0.25rem">
						<Text fz="md" fw={600} c="dark.8">
							{tMipManage('vesting_terms_bulk_drawer.securities_summary')}
						</Text>

						<VestingSecuritySummary
							securities={securitiesList}
							noSecuritiesText={tMipManage(
								'vesting_terms_bulk_drawer.no_securities_text'
							)}
							hideUnits
						/>
					</Flex>

					<FormProvider {...vestingTermBulkForm}>
						<form
							onSubmit={vestingTermBulkForm.handleSubmit(submitBulkUpdates)}
							style={{ height: '100%' }}
						>
							<Flex direction="column" gap="1rem" h="100%" justify="space-between">
								<Flex direction="column" gap="1rem">
									<Select
										name={VESTING_TERMS_BULK_FORM_FIELD.vestingTermId}
										control={vestingTermBulkForm.control}
										size="md"
										rightSection={<Icon variant="caret-down" size="lg" />}
										rightSectionProps={{
											style: { pointerEvents: 'none' },
										}}
										label={tMipManage('investors.expanded_row.vesting_term')}
										placeholder={tMipManage('investors.select')}
										allowDeselect
										data={
											vestingTerms?.map((vestingTerm) => ({
												label: vestingTerm.name,
												value: vestingTerm.id,
											})) ?? []
										}
									/>

									<Flex direction="column" gap="0.25rem">
										<MantineDateInput
											name={VESTING_TERMS_BULK_FORM_FIELD.startDate}
											label={tMipManage(
												'vesting_terms_drawer.vesting_start_date_label'
											)}
											size="md"
											disabled={loading || !formVestingTerm}
											clearable
											placeholder={tMipManage('investors.select')}
											valueFormat="DD/MM/YYYY"
										/>
										<Text fz="xs">
											{tMipManage(
												'vesting_terms_drawer.vesting_default_start_date_info_generic'
											)}
										</Text>
									</Flex>
								</Flex>

								<Flex direction="column" gap="0.5rem" mt="2rem">
									<Button type="submit" fullWidth mt="auto">
										Update
									</Button>
									<Button
										onClick={closeDrawerAndResetForm}
										fullWidth
										variant="default"
									>
										Cancel
									</Button>
								</Flex>
							</Flex>
						</form>
					</FormProvider>
				</Flex>
			}
		/>
	);
};

const VestingDrawerInfoBox = ({
	text,
	rippleColor = 'blue',
}: {
	text: string;
	rippleColor?: RippleColor;
}) => {
	return (
		<InfoBox
			iconVariant="information"
			rippleColor={rippleColor}
			alignContent="center"
			description={text}
		/>
	);
};
